import { isValidDate, safeParseDate } from 'src/utils/safe-json';
import PlatformService from 'src/utils/Services/PlatformDBService';

import { Preferences, PreferencesSchema, getDefaultPreferences } from './types';

// Database configuration key for preferences
const PREFERENCES_DB_KEY = 'app_preferences';

/**
 * Recursively converts Date objects to ISO strings for JSON storage
 */
const serializeDates = (obj: any): any => {
  if (obj === null || obj === undefined) return obj;

  if (obj instanceof Date) {
    return obj.toISOString();
  }

  if (Array.isArray(obj)) {
    return obj.map(serializeDates);
  }

  if (typeof obj === 'object') {
    const result: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      result[key] = serializeDates(value);
    });
    return result;
  }

  return obj;
};

/**
 * Recursively converts ISO date strings back to Date objects
 * Now with validation to prevent Invalid Date objects
 */
const deserializeDates = (obj: any, schema?: any): any => {
  if (obj === null || obj === undefined) return obj;

  // Check if this field should be a Date based on specific date field names
  // NOTE: Do NOT match fields like 'dateFormat' or 'timeFormat' - those are format strings, not dates!
  const isDateField = (key: string) =>
    key === 'lastUpdated' ||
    key === 'lastUsed' ||
    key === 'firstUsed' ||
    key === 'createdAt' ||
    key === 'updatedAt';

  if (typeof obj === 'string') {
    // Check if it's an ISO date string
    const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
    if (dateRegex.test(obj)) {
      // Use safe parser that validates the date
      return safeParseDate(obj);
    }
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => deserializeDates(item, schema));
  }

  if (typeof obj === 'object') {
    const result: any = {};
    Object.entries(obj).forEach(([key, value]) => {
      // Special handling for known date fields
      if (isDateField(key) && typeof value === 'string') {
        const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
        if (dateRegex.test(value)) {
          // Use safe parser with fallback
          result[key] = safeParseDate(value);
        } else {
          // Invalid date string, log and use fallback
          console.warn(`Invalid date string for field '${key}':`, value);
          result[key] = safeParseDate(value);
        }
      } else {
        result[key] = deserializeDates(value, schema);
      }
    });
    return result;
  }

  return obj;
};

/**
 * Save preferences to local database
 */
export const savePreferencesToDB = async (preferences: Preferences): Promise<void> => {
  try {
    const instance = await PlatformService.getInstance();
    const dataToSave = {
      ...preferences,
      lastUpdated: new Date(),
    };

    // Serialize dates before saving
    const serializedData = serializeDates(dataToSave);

    await instance.updateOrCreateSystemConfig(PREFERENCES_DB_KEY, serializedData);
    console.log('Preferences saved to database successfully');
  } catch (error) {
    console.error('Failed to save preferences to database:', error);
    throw new Error('Failed to save preferences to database');
  }
};

/**
 * Extract field paths that failed validation for debugging
 */
const extractCorruptedFields = (data: any): string[] => {
  try {
    PreferencesSchema.parse(data);
    return [];
  } catch (error) {
    if (error instanceof Error && 'issues' in error) {
      const zodError = error as any;
      return zodError.issues?.map((issue: any) => issue.path.join('.')) || [];
    }
    return [];
  }
};

/**
 * Log corruption metrics for monitoring
 * In production, this could send to analytics service
 */
const logDataCorruption = (
  context: string,
  details: {
    corruptionType: 'json_parse' | 'date_invalid' | 'validation_failed';
    affectedFields?: string[];
    errorMessage?: string;
    recoveryAction: 'used_defaults' | 'partial_recovery' | 'deep_merge';
  }
) => {
  const logEntry = {
    timestamp: new Date().toISOString(),
    context,
    ...details,
  };

  console.error('[DATA_CORRUPTION]', JSON.stringify(logEntry, null, 2));

  // TODO: In production, send to analytics/monitoring service
  // analytics.track('preference_corruption', logEntry);
};

/**
 * Deep merge preferences, preserving valid nested data
 * Unlike shallow merge ({ ...defaults, ...data }), this preserves
 * valid nested objects even if parent object has some corruption
 */
const deepMergePreferences = (defaults: Preferences, data: Partial<Preferences>): Preferences => {
  const result = { ...defaults };

  // Merge each category deeply
  const categories: (keyof Preferences)[] = [
    'ui',
    'checkout',
    'inventory',
    'receipts',
    'general',
    'restaurantPos',
  ];

  categories.forEach((category) => {
    if (category in data && data[category] && typeof data[category] === 'object') {
      // Deep merge category (safely cast after validation)
      const defaultCategoryData = defaults[category];
      const dataCategoryData = data[category];

      if (typeof defaultCategoryData === 'object' && typeof dataCategoryData === 'object') {
        result[category] = {
          ...defaultCategoryData,
          ...dataCategoryData,
        } as any;
      }

      // Special handling for nested objects in checkout
      if (category === 'checkout' && data.checkout?.quickPayUsageTracking) {
        const defaultTracking = defaults.checkout.quickPayUsageTracking;
        const dataTracking = data.checkout.quickPayUsageTracking;

        result.checkout.quickPayUsageTracking = {
          ...defaultTracking,
          ...dataTracking,
          // Validate amounts array
          amounts: Array.isArray(dataTracking.amounts)
            ? dataTracking.amounts.filter(
                (item) =>
                  // Validate each amount item
                  typeof item === 'object' &&
                  item !== null &&
                  typeof item.cents === 'number' &&
                  typeof item.usageCount === 'number' &&
                  isValidDate(item.lastUsed) &&
                  isValidDate(item.firstUsed)
              )
            : defaultTracking.amounts,
        };
      }
    }
  });

  // Preserve valid dates
  if (data.lastUpdated && isValidDate(data.lastUpdated)) {
    result.lastUpdated = data.lastUpdated;
  }

  if (data.version) {
    result.version = data.version;
  }

  return result;
};

/**
 * Load preferences from local database
 */
export const loadPreferencesFromDB = async (): Promise<Preferences> => {
  try {
    const instance = await PlatformService.getInstance();
    const result = await instance.getSystemConfig<Preferences>(PREFERENCES_DB_KEY);

    if (result && result.data) {
      // Deserialize dates from stored data
      const deserializedData = deserializeDates(result.data);

      // Validate and merge with defaults to ensure all new fields are present
      try {
        const validated = PreferencesSchema.parse(deserializedData);
        return validated;
      } catch (validationError) {
        // Log corruption details for monitoring
        logDataCorruption('loadPreferencesFromDB:validation', {
          corruptionType: 'validation_failed',
          affectedFields: extractCorruptedFields(deserializedData),
          errorMessage:
            validationError instanceof Error ? validationError.message : 'Unknown validation error',
          recoveryAction: 'deep_merge',
        });

        console.warn('Preferences validation failed, merging with defaults:', validationError);

        // IMPROVED: Deep merge instead of shallow merge
        const defaults = getDefaultPreferences();
        const merged = deepMergePreferences(defaults, deserializedData);

        try {
          // Try validating merged data
          return PreferencesSchema.parse(merged);
        } catch (secondError) {
          // If still failing, log details and return defaults
          console.error('Preferences validation failed after merge, using defaults:', {
            validationError,
            secondError,
            corruptedFields: extractCorruptedFields(deserializedData),
          });

          logDataCorruption('loadPreferencesFromDB:merge_failed', {
            corruptionType: 'validation_failed',
            affectedFields: extractCorruptedFields(merged),
            errorMessage: secondError instanceof Error ? secondError.message : 'Unknown error',
            recoveryAction: 'used_defaults',
          });

          return defaults;
        }
      }
    }

    // Return defaults if no preferences found
    return getDefaultPreferences();
  } catch (error) {
    console.error('Failed to load preferences from database:', error);
    // Return defaults on error
    return getDefaultPreferences();
  }
};

/**
 * Delete all preferences from database (reset to defaults)
 */
export const deletePreferencesFromDB = async (): Promise<void> => {
  try {
    const instance = await PlatformService.getInstance();
    await instance.deleteSystemConfig(PREFERENCES_DB_KEY);
    console.log('Preferences deleted from database successfully');
  } catch (error) {
    console.error('Failed to delete preferences from database:', error);
    throw new Error('Failed to delete preferences from database');
  }
};

/**
 * Check if preferences exist in database
 */
export const preferencesExistInDB = async (): Promise<boolean> => {
  try {
    const instance = await PlatformService.getInstance();
    const result = await instance.getSystemConfig(PREFERENCES_DB_KEY);
    return result !== null;
  } catch (error) {
    console.error('Failed to check preferences existence:', error);
    return false;
  }
};

/**
 * Update specific preference category in database
 */
export const updatePreferenceCategoryInDB = async <T extends keyof Preferences>(
  category: T,
  data: Partial<Preferences[T]>
): Promise<void> => {
  try {
    // Load current preferences (already deserialized)
    const currentPreferences = await loadPreferencesFromDB();

    // Update the specific category
    const updatedPreferences: Preferences = {
      ...currentPreferences,
      [category]: {
        ...(currentPreferences[category] as object),
        ...(data as object),
      },
      lastUpdated: new Date(),
    };

    // Save back to database (will serialize dates)
    await savePreferencesToDB(updatedPreferences);
  } catch (error) {
    console.error(`Failed to update ${category} preferences:`, error);
    throw new Error(`Failed to update ${category} preferences`);
  }
};
